import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import Button from '@ids-ts/button';
import CopyIcon from 'src/assets/images/copy-icon.svg';
import SurveyModal from 'src/components/shared/SurveyModal';
import { createRegistration } from 'src/generated/api';
import { getTeacherPostRegistrationSurveyUrl, getRegistrationDoneUrl } from 'src/config';
import { submitRegistrationSurveyResponses } from 'src/api/registrationSurveyResponses';
import styles from 'src/components/flows/styles/RegistrationSuccess.module.scss';
/* eslint-enable no-unused-vars, react/require-default-props, react/no-unused-prop-types */
// Constants
const CONTENT = {
    title: 'Congrats!',
    school: {
        messagePrefix: "You've successfully registered for the Intuit Hour of Finance Challenge as part of ",
        messageSuffix: '',
    },
    community: {
        messagePrefix: "You've successfully registered as part of ",
        messageSuffix: ' for the Intuit Hour of Finance Challenge.',
    },
    independent: {
        message: "You've successfully registered for the Intuit Hour of Finance Challenge.",
    },
    joinCodeLabel: 'Join Code',
    joinCodeDescription: 'Your students will access the game using the Join code.',
    impactTrackerCodeLabel: 'Impact Tracker Code',
    impactTrackerCodeDescription: "Use this code on the Impact Tracker to see your school's progress",
    doneButtonText: 'Done',
    viewChallengeButtonText: 'View Student Challenge',
    copiedFeedback: 'Copied!',
};
const INFO_BOX = {
    questionText: 'Additional Questions?',
    contactText: 'Email education@intuit.com for assistance.',
    emailAddress: 'education@intuit.com',
};
/**
 * Formats a code string with spaces for better readability
 * @param {string | undefined} code - The code to format
 * @returns {string} Formatted code with spaces every 3 characters
 */
const formatCode = (code) => {
    if (!code)
        return '';
    // Remove any existing spaces and format as XXX XXX XXX
    const cleaned = code.replace(/\s/g, '');
    return cleaned.replace(/(.{3})/g, '$1 ').trim();
};
/**
 * RegistrationSuccess step component
 * Displays success message with join code and impact tracker code after registration
 * @param {RegistrationSuccessProps} props - Component props
 * @param {RegistrationSuccessData} props.initialData - Success data (schoolName, joinCode, dashboardCode)
 * @param {Function} props.setTitle - Callback to set step title
 * @param {Function} props.onPageValid - Callback when page validation changes
 * @param {Function} props.onHideNavigation - Callback to hide navigation buttons
 * @param {Function} props.onDone - Callback when user clicks Done button
 * @param {UserType} props.userType - Type of user registration (school, community, independent)
 * @param {Object} props.registrationData - Registration form data to submit
 * @returns {JSX.Element} RegistrationSuccess component
 */
const RegistrationSuccess = ({ initialData, setTitle, onPageValid, onHideNavigation, userType, registrationData, }) => {
    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [registration, setRegistration] = useState(null);
    const submittedRef = useRef(false);
    // Existing state
    const [copiedCode, setCopiedCode] = useState(null);
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    // Get data from either initialData or registration state
    // School name comes from initialData (passed from findYourSchool step)
    const schoolName = initialData?.schoolName;
    // Codes come from either initialData or registration response
    const joinCode = initialData?.joinCode || registration?.joinCode;
    const impactTrackerCode = initialData?.dashboardCode || registration?.dashboardCode;
    /**
     * Submit registration when component mounts if registrationData is provided
     */
    useEffect(() => {
        /**
         * Submits the registration to the API
         * @returns {Promise<void>} Promise that resolves when registration is complete
         */
        const submitRegistration = async () => {
            // Skip if we already have success data
            if (initialData?.joinCode) {
                setTitle?.(CONTENT.title);
                onPageValid?.(true);
                onHideNavigation?.(true);
                return;
            }
            // Skip if no registration data
            if (!registrationData) {
                setError('Missing registration data');
                setTitle?.('Registration Error');
                onPageValid?.(false);
                onHideNavigation?.(false); // Show navigation so user can go back
                return;
            }
            // Prevent double submission (Strict Mode)
            if (submittedRef.current) {
                return;
            }
            submittedRef.current = true;
            setLoading(true);
            setError(null);
            onHideNavigation?.(true); // Hide navigation while loading
            try {
                const response = await createRegistration(registrationData);
                setRegistration(response.data);
                setTitle?.(CONTENT.title);
                onPageValid?.(true);
                onHideNavigation?.(true); // Hide navigation on success (final step)
            }
            catch (err) {
                setError(err instanceof Error
                    ? err.message
                    : 'Failed to complete registration. Please try again.');
                setTitle?.('Registration Error');
                onPageValid?.(false);
                onHideNavigation?.(false); // Show navigation so user can go back
            }
            finally {
                setLoading(false);
            }
        };
        submitRegistration();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    /**
     * Copies text to clipboard and shows feedback
     * @param {string | undefined} text - Text to copy
     * @param {'join' | 'impact'} codeType - Type of code being copied
     * @returns {Promise<void>} Promise that resolves when copy is complete
     */
    const handleCopyToClipboard = async (text, codeType) => {
        if (!text)
            return;
        try {
            // Copy the raw code without formatting
            await navigator.clipboard.writeText(text.replace(/\s/g, ''));
            setCopiedCode(codeType);
            setTimeout(() => setCopiedCode(null), 2000);
        }
        catch (err) {
            // Silently fail - clipboard API may not be available
        }
    };
    /**
     * Handles the Done button click
     * For independent learners, redirect directly to done URL
     * For other user types, open the survey modal
     * @returns {void}
     */
    const handleDone = () => {
        // Independent learners skip the survey and go straight to the done URL
        if (userType === 'independent') {
            window.location.href = getRegistrationDoneUrl();
            return;
        }
        // For school and community, show the survey modal
        setShowSurveyModal(true);
    };
    /**
     * Handles survey submission
     * For now, just redirect to done URL after submission
     * @param {Record<string, string>} answers - Survey answers
     * @returns {Promise<void>}
     */
    const handleSurveySubmit = async (answers) => {
        if (!joinCode) {
            throw new Error('Missing join code. Please retry registration.');
        }
        await submitRegistrationSurveyResponses({
            joinCode,
            surveyUrl: getTeacherPostRegistrationSurveyUrl(),
            answers,
        });
        setShowSurveyModal(false);
        window.location.href = getRegistrationDoneUrl();
    };
    /**
     * Handles "Not at this time" button click in survey modal
     * Redirects to done URL
     * @returns {void}
     */
    const handleSkipSurvey = () => {
        setShowSurveyModal(false);
        window.location.href = getRegistrationDoneUrl();
    };
    /**
     * Handles the View Student Challenge button click
     * Opens the student challenge in a new tab
     * @returns {void}
     */
    // const handleViewChallenge = () => {
    //   window.open(getStudentPortalUrl(), '_blank');
    // };
    // Loading state
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.content, children: _jsx("div", { className: styles.loadingContainer, children: _jsx("p", { className: styles.loadingText, children: "Submitting your registration..." }) }) }) }));
    }
    // Error state
    if (error) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.content, children: _jsxs("div", { className: styles.errorContainer, children: [_jsx("p", { className: styles.errorText, children: error }), _jsx("p", { className: styles.errorHelp, children: "Please use the Back button to return and try again." })] }) }) }));
    }
    /**
     * Render success message based on user type
     * @returns {JSX.Element} Success message element
     */
    const renderSuccessMessage = () => {
        if (userType === 'independent') {
            return (_jsx("p", { className: styles.successMessage, children: CONTENT.independent.message }));
        }
        if (userType === 'school') {
            return (_jsxs("p", { className: styles.successMessage, children: [CONTENT.school.messagePrefix, _jsx("span", { className: styles.schoolName, children: schoolName }), CONTENT.school.messageSuffix] }));
        }
        if (userType === 'community') {
            return (_jsxs("p", { className: styles.successMessage, children: [CONTENT.community.messagePrefix, _jsx("span", { className: styles.schoolName, children: schoolName }), CONTENT.community.messageSuffix] }));
        }
        return null;
    };
    // Success state
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.content, children: [renderSuccessMessage(), _jsxs("div", { className: styles.codesContainer, children: [_jsxs("div", { className: styles.codeSection, children: [_jsx("p", { className: styles.codeLabel, children: CONTENT.joinCodeLabel }), _jsxs("div", { className: styles.codeCard, children: [_jsxs("button", { type: "button", className: styles.codeButton, onClick: () => handleCopyToClipboard(joinCode, 'join'), "aria-label": "Copy Join Code", children: [_jsx("span", { className: styles.codeText, children: formatCode(joinCode) }), _jsx("img", { src: CopyIcon, alt: "", className: styles.copyIcon, "aria-hidden": "true" })] }), copiedCode === 'join' && (_jsx("span", { className: styles.copiedFeedback, children: CONTENT.copiedFeedback }))] }), _jsx("p", { className: styles.codeDescription, children: CONTENT.joinCodeDescription })] }), _jsxs("div", { className: styles.codeSection, children: [_jsx("p", { className: styles.codeLabel, children: CONTENT.impactTrackerCodeLabel }), _jsxs("div", { className: styles.codeCard, children: [_jsxs("button", { type: "button", className: styles.codeButton, onClick: () => handleCopyToClipboard(impactTrackerCode, 'impact'), "aria-label": "Copy Impact Tracker Code", children: [_jsx("span", { className: styles.codeText, children: formatCode(impactTrackerCode) }), _jsx("img", { src: CopyIcon, alt: "", className: styles.copyIcon, "aria-hidden": "true" })] }), copiedCode === 'impact' && (_jsx("span", { className: styles.copiedFeedback, children: CONTENT.copiedFeedback }))] }), _jsx("p", { className: styles.codeDescription, children: CONTENT.impactTrackerCodeDescription })] })] }), _jsx("div", { className: styles.buttonContainer, children: _jsx(Button, { className: styles.doneButton, onClick: handleDone, children: CONTENT.doneButtonText }) }), _jsx("div", { className: styles.infoBox, children: _jsxs("p", { className: styles.infoContent, children: [_jsx("span", { className: styles.infoQuestion, children: INFO_BOX.questionText }), _jsxs("span", { className: styles.infoContact, children: ["Email", ' ', _jsx("a", { href: `mailto:${INFO_BOX.emailAddress}`, className: styles.infoLink, children: INFO_BOX.emailAddress }), ' ', "for assistance."] })] }) })] }), _jsx(SurveyModal, { isOpen: showSurveyModal, onClose: () => setShowSurveyModal(false), surveyUrl: getTeacherPostRegistrationSurveyUrl(), onSubmit: handleSurveySubmit, onSkip: handleSkipSurvey })] }));
};
export default RegistrationSuccess;
